import { useMemo } from 'react';
import { NavigateOptions, useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from 'state/hooks';
import { selectProject } from 'state/workflowSlice';

import {
  assignRouteUrl,
  assignRoutesUrl,
  editAlertUrl,
  manageAlertsUrl,
  newAlertUrl,
  notificationLogUrl,
  viewAlertUrl,
} from './alertHelpers';

/** Uses :projectSlug from URL path when under /notifications, otherwise falls back to active project in context */
export function useNotificationProject() {
  const navigate = useNavigate();
  const { projectSlug: projectSlugParam } = useParams<{
    projectSlug?: string;
  }>();
  const projectSlugFromStore = useAppSelector(selectProject);
  const projectSlug = projectSlugParam ?? projectSlugFromStore;

  const navigateTo = useMemo(
    () => ({
      log: (options?: NavigateOptions) => {
        if (projectSlug) navigate(notificationLogUrl(projectSlug), options);
      },
      manage: () => {
        if (projectSlug) navigate(manageAlertsUrl(projectSlug));
      },
      routes: () => {
        if (projectSlug) navigate(assignRoutesUrl(projectSlug));
      },
      newAlert: () => {
        if (projectSlug) navigate(newAlertUrl(projectSlug));
      },
      viewAlert: (alertId: string) => {
        if (projectSlug) navigate(viewAlertUrl(projectSlug, alertId));
      },
      editAlert: (alertId: string) => {
        if (projectSlug) navigate(editAlertUrl(projectSlug, alertId));
      },
      assignRoute: (routeId: string) => {
        if (projectSlug) navigate(assignRouteUrl(projectSlug, routeId));
      },
    }),
    [navigate, projectSlug],
  );

  const urls = useMemo(
    () =>
      projectSlug
        ? {
            log: notificationLogUrl(projectSlug),
            manage: manageAlertsUrl(projectSlug),
            routes: assignRoutesUrl(projectSlug),
            new: newAlertUrl(projectSlug),
            viewAlert: (alertId: string) => viewAlertUrl(projectSlug, alertId),
            editAlert: (alertId: string) => editAlertUrl(projectSlug, alertId),
            assignRoute: (routeId: string) =>
              assignRouteUrl(projectSlug, routeId),
          }
        : null,
    [projectSlug],
  );

  return { projectSlug, navigateTo, urls };
}
